import React, { useMemo } from "react";
import { renderToString } from "react-dom/server";
import { useTheme } from "@mui/material";
import { Options as HighchartsOptions, Chart } from "highcharts";
import { GaugeLabel } from "./GaugeLabel";
import { drawOrUpdateEndCircle } from "./helper";

export const useChartOptions = (
  width: number,
  height: number,
  percentage: number,
  centerPanel: {
    color: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
  },
  handlerCircle: {
    radiusOffset: number;
    centerDistanceOffset: number;
  }
): HighchartsOptions => {
  const theme = useTheme();
  const { color, title, subtitle, icon } = centerPanel;
  const angleSpan = 270;
  const valueAngle = (percentage / 100) * angleSpan;
  const remainingAngle = angleSpan - valueAngle;

  const trackColor = theme.palette.background.neutralDark;

  return useMemo(
    () => ({
      credits: {
        enabled: false,
      },
      chart: {
        subtitle: {
          text: "",
          useHTML: true,
          align: "center",
          verticalAlign: "middle",
        },
        events: {
          load: function () {
            const chart = this as Chart;
            chart.setSubtitle({
              text: renderToString(
                <GaugeLabel
                  theme={theme}
                  title={title}
                  subtitle={subtitle}
                  icon={icon}
                />
              ),
              useHTML: true,
              align: "center",
              verticalAlign: "middle",
            });
            drawOrUpdateEndCircle(chart, theme, handlerCircle);
          },
          redraw: function () {
            drawOrUpdateEndCircle(this as Chart, theme, handlerCircle);
          },
        },
        plotShadow: false,
        type: "pie",
        width,
        height,
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
      },
      title: {
        text: "",
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: () => false,
      },
      colors: [color, trackColor],
      accessibility: {
        point: {
          valueSuffix: "%",
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: "pointer",
          borderWidth: 0,
          size: "100%",
          innerSize: "85%",
          startAngle: -135,
          endAngle: 135,
          borderRadius: 10,
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          enableMouseTracking: false,
          cursor: "default",
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      series: [
        {
          type: "pie",
          data: [
            {
              y: valueAngle,
              color: color,
            },
            {
              y: remainingAngle,
              color: trackColor,
            },
          ],
        },
      ],
    }),
    [
      width,
      height,
      color,
      theme,
      valueAngle,
      remainingAngle,
      trackColor,
      title,
      subtitle,
      icon,
      handlerCircle,
    ]
  );
};
